import { Component, ChangeDetectionStrategy, inject, signal, computed, viewChild, effect, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { AuthService } from '@services/auth/auth.service';
import { RecaptchaService } from '@services/recaptcha/recaptcha.service';
import { environment } from '@environments/environment';

@Component({
    selector: 'app-login',
    imports: [CommonModule, ReactiveFormsModule, InputTextModule, PasswordModule, ButtonModule, MessageModule],
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { '[class.flying]': 'flying()' }
})
export class LoginComponent {
    private readonly fb = inject(FormBuilder);
    private readonly authService = inject(AuthService);
    private readonly router = inject(Router);
    private readonly recaptchaService = inject(RecaptchaService);

    readonly logoPath = environment.logo;
    readonly companyName = environment.companyName;
    readonly recaptchaSiteKey = environment.recaptchaSiteKey;
    readonly forgotPasswordUrl = `${environment.mainDashboard}/forgotPassword`;
    readonly loading = signal(false);
    readonly flying = signal(false);
    readonly errorMessage = signal<string | undefined>(undefined);
    readonly recaptchaVerified = this.recaptchaService.isVerified;
    readonly failedAttempts = signal(0);
    readonly showRecaptcha = computed(() => this.failedAttempts() >= 2 && !!this.recaptchaSiteKey);

    private readonly recaptchaContainer = viewChild<ElementRef<HTMLElement>>('recaptchaContainer');

    constructor() {
        effect(() => {
            const container = this.recaptchaContainer();
            if (container && this.showRecaptcha()) {
                this.recaptchaService.render(container.nativeElement, this.recaptchaSiteKey);
            }
        });
    }

    readonly loginForm = this.fb.nonNullable.group({
        username: ['', [Validators.required]],
        password: ['', [Validators.required]]
    });

    onSubmit(): void {
        if (this.loginForm.invalid) {
            this.loginForm.markAllAsTouched();
            return;
        }

        if (this.showRecaptcha() && !this.recaptchaVerified()) {
            this.errorMessage.set('Please complete the reCAPTCHA verification.');
            return;
        }

        this.loading.set(true);
        this.errorMessage.set(undefined);

        const { username, password } = this.loginForm.getRawValue();
        const recaptchaToken = this.showRecaptcha() ? this.recaptchaService.getResponse() : undefined;

        this.authService.login(username, password, recaptchaToken).subscribe({
            next: (response) => {
                this.loading.set(false);

                if (response.token) {
                    this.flying.set(true);
                    setTimeout(() => this.router.navigate(['/']), 1300);
                } else {
                    this.errorMessage.set('Login failed. Please try again.');
                }
            },
            error: (error) => {
                this.loading.set(false);
                this.failedAttempts.update((count) => count + 1);
                if (this.showRecaptcha()) {
                    this.recaptchaService.reset();
                }
                if (error.status === 423) {
                    this.errorMessage.set('Your account is locked. Please contact your administrator.');
                } else if (error.status === 401) {
                    this.errorMessage.set('Invalid username or password.');
                } else {
                    this.errorMessage.set('Login failed. Please try again later.');
                }
            }
        });
    }
}
